import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../api.types';
import {
  ViewActivityEngagementRequest,
  ViewActivityEngagementResponse,
  ViewHashtagEngagementRequest,
  ViewHashtagEngagementResponse,
  ViewHashtagOccurencesRequest,
  ViewHashtagOccurencesResponse
} from './administration.types';

import { RETRIEVING_ACTIVITY_ENGAGEMENT, RETRIEVING_HASHTAG_ENGAGEMENT } from './administration.constants';

import {
  requestViewActivityEngagement,
  requestViewHashtagEngagement,
  requestViewHashtagOccurrences
} from './administration.actions';

import {
  getViewActivityEngagementRequest,
  getViewHashtagEngagementRequest,
  getViewHashtagOccurencesRequest
} from './administration.endpoints';

import {
  setActivityEngagement,
  setHashtagEngagement,
  setHashtagOccurrences
} from '../../slices/programme/tribe/tribe.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR } from '../../utils/constants';

export function* performViewActivityEngagementRequest({ payload }: SagaPayload<ViewActivityEngagementRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_ACTIVITY_ENGAGEMENT));

    const [endpoint, options] = getViewActivityEngagementRequest(requestData);

    const { data }: AxiosResponse<ViewActivityEngagementResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setActivityEngagement(data.memberEngagements));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_ACTIVITY_ENGAGEMENT));
  }
}

export function* watchForViewActivityEngagementRequest() {
  yield takeLatest(requestViewActivityEngagement.type, performViewActivityEngagementRequest);
}

export function* performViewHashtagEngagementRequest({ payload }: SagaPayload<ViewHashtagEngagementRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_HASHTAG_ENGAGEMENT));

    const [endpoint, options] = getViewHashtagEngagementRequest(requestData);

    const { data }: AxiosResponse<ViewHashtagEngagementResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setHashtagEngagement(data.memberHashTags));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_HASHTAG_ENGAGEMENT));
  }
}

export function* watchForViewHashtagEngagementRequest() {
  yield takeLatest(requestViewHashtagEngagement.type, performViewHashtagEngagementRequest);
}

export function* performViewHashtagOccurrencesRequest({ payload }: SagaPayload<ViewHashtagOccurencesRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    const [endpoint, options] = getViewHashtagOccurencesRequest(requestData);

    const { data }: AxiosResponse<ViewHashtagOccurencesResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setHashtagOccurrences(data.hashTagOccurrences));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewHashtagOccurrencesRequest() {
  yield takeLatest(requestViewHashtagOccurrences.type, performViewHashtagOccurrencesRequest);
}

export default function* administrationSaga() {
  yield all([
    watchForViewActivityEngagementRequest(),
    watchForViewHashtagEngagementRequest(),
    watchForViewHashtagOccurrencesRequest()
  ]);
}
